/**
 * JSON-LD builders for the dataset catalogue.
 *
 * WHY THIS EXISTS
 * ---------------
 * Google Dataset Search indexes exactly one schema.org type: `Dataset`. It is a
 * separate index from web search, with its own crawler expectations, and it is
 * the single highest-leverage structured-data win available to a data catalogue.
 * Nothing on this site emitted it before.
 *
 * Everything here is derived from `src/app/data/datasets.ts` — the registry is
 * still the single source of truth. Do not hardcode dataset facts in this file.
 *
 * HONESTY RULE (inherited from the registry)
 * ------------------------------------------
 * Fields that are `undefined` in the registry are OMITTED from the JSON-LD, never
 * filled with a plausible default. A wrong `temporalCoverage` in structured data
 * is worse than an absent one: it is a machine-readable claim to third parties.
 *
 * PUBLIC EXPORTS
 * --------------
 *   SITE                 Canonical site constants (origin, org, license, logo).
 *   canonicalUrl(path)   Absolute URL for a route path. Use for canonicals.
 *   datasetUrl(slug)     Absolute URL of a dataset detail page.
 *   datasetJsonLd(d)     schema.org `Dataset` node for one dataset.
 *   dataCatalogJsonLd()  schema.org `DataCatalog` node listing every dataset.
 *   organizationJsonLd() schema.org `Organization` node for SOVAI.
 *   breadcrumbJsonLd()   schema.org `BreadcrumbList` helper.
 *
 * USAGE (DatasetDetail page)
 * --------------------------
 *   import { Seo } from 'app/components/Seo';
 *   import { datasetJsonLd, breadcrumbJsonLd } from 'app/components/Seo/datasetJsonLd';
 *
 *   <Seo
 *     title={dataset.name}
 *     description={dataset.summary}
 *     path={`/datasets/${dataset.slug}`}
 *     jsonLd={[
 *       datasetJsonLd(dataset),
 *       breadcrumbJsonLd([
 *         { name: 'Data catalogue', path: '/datasets' },
 *         { name: dataset.name, path: `/datasets/${dataset.slug}` },
 *       ]),
 *     ]}
 *   />
 *
 * The returned objects are plain JSON-serialisable values. `<Seo>` stringifies
 * them; if you emit them yourself, remember to escape `<` in the script body.
 */

import { Dataset, datasets, catalogueStats } from 'app/data/datasets';

/** A JSON-LD node. Loose by design — schema.org shapes are open. */
export type JsonLd = Record<string, unknown>;

/**
 * Canonical site constants. Everything that needs an absolute URL goes through
 * here so there is exactly one place to change if the origin ever moves.
 *
 * `licenseUrl` is the published terms of service (linked from the site footer),
 * which is the document that actually governs use of the data. Google treats a
 * missing `license` on a Dataset as a quality signal against it.
 */
export const SITE = {
  origin: 'https://sov.ai',
  name: 'SOVAI',
  legalName: 'SOVAI',
  logo: 'https://sov.ai/logo192.png',
  defaultImage: 'https://sov.ai/og_image.jpg',
  licenseUrl: 'https://land.sov.ai/terms-of-service',
  privacyUrl: 'https://land.sov.ai/privacy-policy',
  docsUrl: 'https://docs.sov.ai',
  sameAs: [
    'https://www.linkedin.com/company/sovai',
    'https://twitter.com/sovai',
  ],
} as const;

/**
 * Absolute, canonical URL for a route path.
 * Normalises leading slash and strips a trailing slash (except for the root),
 * so `/datasets/`, `datasets` and `/datasets` all produce one canonical form.
 */
export const canonicalUrl = (path = '/'): string => {
  const withSlash = path.startsWith('/') ? path : `/${path}`;
  const trimmed = withSlash.length > 1 ? withSlash.replace(/\/+$/, '') : '/';
  return `${SITE.origin}${trimmed}`;
};

/** Absolute URL of a dataset detail page. */
export const datasetUrl = (slug: string): string =>
  canonicalUrl(`/datasets/${slug}`);

/** Stable, non-URL identifier for a dataset. Used as schema.org `identifier`. */
export const datasetIdentifier = (slug: string): string => `sovai:${slug}`;

const CATEGORY_LABEL: Record<Dataset['category'], string> = {
  equity: 'Equity',
  economic: 'Economic',
  sectorial: 'Sectorial',
};

/**
 * SEO description for a Dataset node.
 *
 * Google Dataset Search rejects a `description` shorter than 50 characters
 * ("Invalid string length"). Several registry summaries are deliberately terse
 * (e.g. "Composite risk measures at the issuer level." = 44 chars) because they
 * read better in the dense UI table — but that same string is what we hand to
 * the structured-data crawler.
 *
 * Rather than pad the UI copy, we append a factual, always-true clause built
 * only from fields the registry is guaranteed to have. It invents nothing: every
 * dataset in the catalogue is category-typed, ticker-linked and SDK-delivered.
 * The result clears the 50-char floor for the shortest summary and gives Google
 * a richer description for the rest.
 */
export const datasetSeoDescription = (dataset: Dataset): string =>
  `${dataset.summary} ${CATEGORY_LABEL[dataset.category]} alternative data ` +
  `from the SOVAI catalogue — linked to exchange-listed company tickers and ` +
  `delivered through the sovai Python SDK.`;

/** The Organization node, reused as creator/publisher on every other node. */
export const organizationJsonLd = (): JsonLd => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE.origin}/#organization`,
  name: SITE.name,
  legalName: SITE.legalName,
  url: SITE.origin,
  logo: SITE.logo,
  description:
    'SOVAI publishes a catalogue of ticker-linked alternative datasets for ' +
    'asset managers, delivered through a Python SDK.',
  sameAs: [...SITE.sameAs],
});

/** Compact Organization reference for embedding inside other nodes. */
const orgRef = (): JsonLd => ({
  '@type': 'Organization',
  '@id': `${SITE.origin}/#organization`,
  name: SITE.name,
  url: SITE.origin,
});

/**
 * schema.org `Dataset` for a single registry entry.
 *
 * Field mapping, and why each one is there:
 *   name              registry name
 *   description       registry summary (Google requires a description)
 *   url               the canonical detail page, not the docs page
 *   identifier        stable `sovai:<slug>` handle
 *   temporalCoverage  ISO 8601 open-ended interval from historyStart, e.g.
 *                     "1985-01-01/.." — omitted entirely when history is unknown
 *   license           published terms of service
 *   distribution      the real access channel: the Python SDK call
 *   creator/publisher SOVAI
 *   includedInDataCatalog  links the dataset back to the catalogue node
 *   isAccessibleForFree    false — this is a paid catalogue; claiming free is a
 *                          misrepresentation Google can act on
 *   variableMeasured  emitted only if a column schema has been published
 *   spatialCoverage   derived from the registry `universe` (US-centric)
 */
export const datasetJsonLd = (dataset: Dataset): JsonLd => {
  const node: JsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    '@id': `${datasetUrl(dataset.slug)}#dataset`,
    name: dataset.name,
    description: datasetSeoDescription(dataset),
    url: datasetUrl(dataset.slug),
    identifier: datasetIdentifier(dataset.slug),
    license: SITE.licenseUrl,
    isAccessibleForFree: false,
    creator: orgRef(),
    publisher: orgRef(),
    includedInDataCatalog: {
      '@type': 'DataCatalog',
      '@id': `${canonicalUrl('/datasets')}#catalog`,
      name: 'SOVAI Data Catalogue',
      url: canonicalUrl('/datasets'),
    },
    keywords: [
      'alternative data',
      'quantitative finance',
      'asset management',
      `${CATEGORY_LABEL[dataset.category]} data`,
      dataset.name,
    ],
    distribution: [
      {
        '@type': 'DataDownload',
        name: `${dataset.name} via the SOVAI Python SDK`,
        // The genuine distribution channel is the SDK, not a file download.
        // Describing it accurately beats inventing a .csv contentUrl.
        encodingFormat: 'application/x-parquet',
        contentUrl: dataset.docsUrl,
        description: `sov.data("${dataset.sdkQuery}")`,
      },
    ],
  };

  // --- Conditional fields: present only when the registry actually knows ---

  if (dataset.historyStart) {
    // ISO 8601 open-ended interval. Google's documented form for "start to now".
    node.temporalCoverage = `${dataset.historyStart}/..`;
    node.datePublished = dataset.historyStart;
  }

  if (dataset.universe) {
    node.spatialCoverage = {
      '@type': 'Place',
      name: dataset.universe,
      address: { '@type': 'PostalAddress', addressCountry: 'US' },
    };
  }

  if (dataset.updateFrequency) {
    // schema.org uses `Dataset.datasetTimeInterval`/Dublin Core `accrualPeriodicity`.
    node.accrualPeriodicity = dataset.updateFrequency;
  }

  if (dataset.entities !== undefined) {
    node.size = `${dataset.entities.toLocaleString('en-US')} entities`;
  }

  if (dataset.schema && dataset.schema.length > 0) {
    node.variableMeasured = dataset.schema.map(column => ({
      '@type': 'PropertyValue',
      name: column.name,
      ...(column.description ? { description: column.description } : {}),
    }));
  }

  return node;
};

/**
 * schema.org `DataCatalog` for the whole site.
 *
 * Emitted on the homepage and on /datasets. It enumerates every dataset by
 * reference (`@id` + url + name) rather than inlining full Dataset nodes, which
 * keeps the payload small while still giving crawlers a discoverable index.
 */
export const dataCatalogJsonLd = (): JsonLd => {
  const stats = catalogueStats();
  return {
    '@context': 'https://schema.org',
    '@type': 'DataCatalog',
    '@id': `${canonicalUrl('/datasets')}#catalog`,
    name: 'SOVAI Data Catalogue',
    alternateName: 'SOVAI alternative data catalogue',
    url: canonicalUrl('/datasets'),
    description:
      `A catalogue of ${stats.total} ticker-linked alternative datasets for ` +
      `asset managers, spanning equity, economic and sectorial coverage with ` +
      `history back to ${stats.earliestHistory.slice(0, 4)}.`,
    license: SITE.licenseUrl,
    provider: orgRef(),
    publisher: orgRef(),
    inLanguage: 'en',
    keywords: [
      'alternative data',
      'financial datasets',
      'quantitative research',
      'asset management',
      'ticker-linked data',
    ],
    dataset: datasets.map(d => ({
      '@type': 'Dataset',
      '@id': `${datasetUrl(d.slug)}#dataset`,
      name: d.name,
      description: datasetSeoDescription(d),
      url: datasetUrl(d.slug),
      identifier: datasetIdentifier(d.slug),
    })),
  };
};

/**
 * schema.org `BreadcrumbList`. Pass the trail excluding the site root, which is
 * prepended automatically.
 */
export const breadcrumbJsonLd = (
  trail: Array<{ name: string; path: string }>,
): JsonLd => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [{ name: 'Home', path: '/' }, ...trail].map(
    (crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: canonicalUrl(crumb.path),
    }),
  ),
});

/**
 * schema.org `WebSite`. Kept minimal — no SearchAction, because the site has no
 * documented search endpoint and declaring a fake one is a manual-action risk.
 */
export const webSiteJsonLd = (): JsonLd => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE.origin}/#website`,
  name: SITE.name,
  url: SITE.origin,
  publisher: orgRef(),
  inLanguage: 'en',
});
